import classes from 'components/Dashboards/Portfolio/PortfolioOverview/PortfolioOverview.module.scss';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { serializeScoreCardsData } from 'components/Dashboards/Portfolio/PortfolioOverview/helper';
import { columns, initialState } from 'components/widgets/ProjectScorecards/projectScorecards.utils';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryProjectScoreCardsSummary } from 'api/queries/getProjectScoreCardsSummary';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IPortfolioProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { portfolioConfig } from 'components/Dashboards/Portfolio/portfolioConfig';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import useTableCurrentState from 'hooks/useTableCurrentState';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { useMutationSetWidgetColumns } from 'components/Dashboards/Project/Components/CustomDashboard/queries/useQuerySelectedTableColumns';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { getStringWithoutHash } from 'utilitys/helpers/general';
const scoreCardId = 'score-card';

const ProjectScorecards = ({
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    filter,
}: IPortfolioProps) => {
    const [isInstance, setIsInstance] = useState(false);
    const tableRef = useRef(null);
    const globalFilterData = useGlobalFilterDataStore((state) => state.globalFilterData);
    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters || []
                : getFiltersData({
                      [FilterNames.TAG_PORTFOLIO_BOARD]: globalFilterData?.programTags?.map((item) => item.value),
                  }),

        [filter, globalFilterData],
    );
    const {
        data: scoreCards,
        isLoading: isLoadingScoreCards,
        isFetching: isFetchingScoreCards,
    } = useQueryProjectScoreCardsSummary({
        filter: {
            filters,
        },
    });
    const route = `${getStringWithoutHash(window.location.href)}#${scoreCardId}`;
    const componentKey = 'projectScorecards';
    const title = 'Project Scorecards';
    const subTitleForExport = `${portfolioConfig.portfolioOverview.title}, ${prepareVersionSelectTitle()}`;
    const scoreCardsTableData = useMemo(() => {
        if (scoreCards) {
            return serializeScoreCardsData(scoreCards?.componentsList);
        }
        return [];
    }, [scoreCards]);
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });

    const setWidgetColumns = (widgetId: string) => {
        if (tableRef.current) {
            const { hiddenColumns, columnOrder } = tableRef.current.state;
            mutateSetWidgetColumns(
                { columns: { hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };
    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle: subTitleForExport, title });
    }, [widgetId, subTitleForExport, title]);

    const handleInstance = (instance) => {
        if (instance && !isInstance) {
            tableRef.current = instance;
            setIsInstance(true);
        }
    };

    return (
        <WidgetWithTitle
            title={title}
            tooltip={scoreCards?.info}
            id={scoreCardId}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    widgetId={widgetId}
                    route={route}
                    projectId={null}
                    contractId={null}
                    callBack={setWidgetColumns}
                    filters={filters}
                />,
                <ShareInsightButton
                    key={'shareInsight'}
                    title={title}
                    link={`${getStringWithoutHash(window.location.href)}#${scoreCardId}`}
                />,
                <ExportDropdown title={title} key={'export'} subTitle={subTitleForExport} />,
            ]}
            titleFilters={[editNarrative && <div key={'editNarrative'}>{editNarrative}</div>]}
        >
            <div data-testid={'table-container'} className={classes.tableContainer}>
                {updatedInitialState && (
                    <MainTable
                        getInstanceCallback={handleInstance}
                        initialState={updatedInitialState}
                        columns={columns}
                        data={scoreCardsTableData}
                        isLoading={isLoadingScoreCards || isFetchingScoreCards}
                        pageSize={1000}
                    />
                )}

                {tableRef?.current && (
                    <CustomTableColumnsModal
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                    />
                )}
            </div>
        </WidgetWithTitle>
    );
};
export default React.memo(ProjectScorecards);
